import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AgreementReturnModel } from "../models/agreement";
import { Location as TQSLocation } from "../models/location";
import { getQualityAlertsByLocations } from "./qualityAlertSlice";
import { QualityAlert } from "../models/quality-alert";
import { getAgreementsBySupplier, selectAgreements } from "./supplierSlice";
import { AppDispatch, RootState } from "./store";

// Initial state type
interface LocationState {
  locations: TQSLocation[];
  filteredLocations: TQSLocation[];
  oem: { id: number; name: string }[];
  selectedOems: { id: number; name: string }[];
  search: string;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: LocationState = {
  locations: [],
  filteredLocations: [],
  oem: [],
  selectedOems: [],
  search: "",
  isLoading: false,
  error: null
};

// Thunks
export const processAgreements = createAsyncThunk(
  'location/processAgreements',
  async (agreements: AgreementReturnModel[] | undefined, { getState, dispatch }) => {
    const state = getState() as RootState;
    const selectedSupplier = state.supplier.selectedSupplier;
    const selectedProducingFacilities = state.supplier.selectedProducingFacilities;
    
    if (!selectedSupplier) {
      throw new Error('No supplier selected');
    }

    // Use passed agreements or get from slice
    const agreementsToProcess = agreements ? agreements : selectAgreements(state);
    if (!agreementsToProcess?.length) {
      return [];
    }

    // Create complete deep copy of agreements
    const agreementsCopy = JSON.parse(JSON.stringify(agreementsToProcess)) as AgreementReturnModel[];

    // Filter and map the locations
    const data: TQSLocation[] = agreementsCopy
      .filter((a: AgreementReturnModel) => a?.agreement?.location && (
        !a.producingFacility ||
        a.producingFacility.some((p: number) =>
          selectedProducingFacilities.map((s) => s?.id).includes(p),
        )
      ))
      .map((a: AgreementReturnModel) => {
        const location = { ...a.agreement.location };
        location.agreement = a.agreement;
        return location;
      })
      .filter((value: TQSLocation | null, index: number, self: (TQSLocation | null)[]) => 
        value && 
        self.findIndex((v: TQSLocation | null) => v?.id === value?.id) === index
      );

    if (data.length > 0) {
      try {
        const qualityAlertsResult = await dispatch(getQualityAlertsByLocations({
          locationIds: data.map((l) => l.id).filter(Boolean),
          supplierId: selectedSupplier.id
        }));
        
        if (qualityAlertsResult.type.endsWith('/rejected')) {
          console.error('Error fetching quality alerts:', qualityAlertsResult.payload);
        } else {
          const qualityAlerts = qualityAlertsResult.payload as QualityAlert[];
          data.forEach((l) => {
            if (l) {
              l.qualityAlerts = qualityAlerts.filter((q: QualityAlert) => q?.locationId === l?.id);
            }
          });
        }
      } catch (error) {
        console.error('Error fetching quality alerts:', error);
        // Don't throw the error, just continue without quality alerts
      }
    }

    return data;
  }
);

// Helper function to check if a location belongs to the "Other" category
const isOtherLocation = (location: TQSLocation) => {
  return !location.oem || (location.oem && location.oem.name === "Other");
};

// Slice
const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    setSearch: (state, action) => {
      state.search = action.payload;
      // If no OEMs are selected, show all locations with search filter
      state.filteredLocations = state.selectedOems.length === 0
        ? state.locations.filter(l => l.name.toLowerCase().includes(action.payload.toLowerCase()))
        : state.locations
            .filter((l) => {
              // Check if location should be included based on selected OEMs
              const selectedOemIds = state.selectedOems.map((o) => o.id);
              
              // If "Other" is selected (ID 19), include locations with null OEM or OEM named "Other"
              if (selectedOemIds.includes(19) && isOtherLocation(l)) {
                return true;
              }
              
              // Include locations whose OEM ID is in the selected OEMs list
              return l.oem && selectedOemIds.includes(l.oem.id);
            })
            .filter(l => l.name.toLowerCase().includes(action.payload.toLowerCase()));
    },
    setSelectedOems: (state, action) => {
      state.selectedOems = action.payload;

      // If no OEMs are selected, show all locations
      state.filteredLocations = state.selectedOems.length === 0 
        ? state.locations.filter(l => l.name.toLowerCase().includes(state.search.toLowerCase()))
        : state.locations
            .filter((l) => {
              // Check if location should be included based on selected OEMs
              const selectedOemIds = state.selectedOems.map((o: { id: number }) => o.id);
              
              // If "Other" is selected (ID 19), include locations with null OEM or OEM named "Other"
              if (selectedOemIds.includes(19) && isOtherLocation(l)) {
                return true;
              }
              
              // Include locations whose OEM ID is in the selected OEMs list
              return l.oem && selectedOemIds.includes(l.oem.id);
            })
            .filter(l => l.name.toLowerCase().includes(state.search.toLowerCase()));
    },
    resetFilter: (state) => {
      state.selectedOems = state.oem;
      state.search = "";
      state.filteredLocations = state.locations;
    },
    updateSelectedOems: (state, action) => {
      const { checked, oem } = action.payload;
      if (checked) {
        state.selectedOems = [...state.selectedOems, oem];
      } else {
        state.selectedOems = state.selectedOems.filter((o) => o.id !== oem.id);
      }
      // If no OEMs are selected, show all locations
      state.filteredLocations = state.selectedOems.length === 0
        ? state.locations.filter(l => l.name.toLowerCase().includes(state.search.toLowerCase()))
        : state.locations
            .filter((l) => {
              // Check if location should be included based on selected OEMs
              const selectedOemIds = state.selectedOems.map((o) => o.id);
              
              // If "Other" is selected (ID 19), include locations with null OEM or OEM named "Other"
              if (selectedOemIds.includes(19) && isOtherLocation(l)) {
                return true;
              }
              
              // Include locations whose OEM ID is in the selected OEMs list
              return l.oem && selectedOemIds.includes(l.oem.id);
            })
            .filter(l => l.name.toLowerCase().includes(state.search.toLowerCase()));
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(processAgreements.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(processAgreements.fulfilled, (state, action) => {
        state.locations = action.payload;
        state.filteredLocations = action.payload;
        
        // Process OEMs
        const oemsFromLocations = action.payload
          .filter((l) => l.oem)
          .map((l) => ({ id: l.oem.id, name: l.oem.name }))
          .filter((value, index, self) => self.findIndex((v) => v.id === value.id) === index)
          .sort((a, b) => a.name.localeCompare(b.name));
        
        // Check if we have any locations with null OEM or OEM named "Other"
        const hasNullOrOtherOem = action.payload.some(
          (l) => !l.oem || (l.oem && l.oem.name === "Other")
        );
        
        // Create final OEM list, adding "Other" category if needed
        let oems = [...oemsFromLocations];
        
        // If we have null OEMs or an OEM named "Other", add a single "Other" entry
        if (hasNullOrOtherOem && !oems.some(o => o.name === "Other")) {
          oems.push({ id: 19, name: "Other" });
        }
        
        // Sort the final list
        oems.sort((a, b) => a.name.localeCompare(b.name));
        
        state.oem = oems;
        state.selectedOems = oems;
        state.isLoading = false;
      })
      .addCase(processAgreements.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to process agreements";
      });
  }
});

// Export actions and reducer
export const { 
  setSearch, 
  setSelectedOems, 
  resetFilter, 
  updateSelectedOems 
} = locationSlice.actions;

// Selectors
export const selectLocations = (state: RootState) => state.location.locations;
export const selectFilteredLocations = (state: RootState) => state.location.filteredLocations;
export const selectOem = (state: RootState) => state.location.oem;
export const selectSelectedOems = (state: RootState) => state.location.selectedOems;
export const selectSearch = (state: RootState) => state.location.search;
export const selectIsLoading = (state: RootState) => state.location.isLoading;
export const selectError = (state: RootState) => state.location.error;

export default locationSlice.reducer; 